import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, MoreHorizontal, Pen, RotateCw, Search, SlidersHorizontal, X } from 'lucide-react';
import { toast } from 'sonner';
import Page from 'components/shared/Page';
import Pagination from 'components/shared/Pagination';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import EmptyCard from 'components/shared/EmptyCard';
import { LockableDropdownMenuItem } from 'components/shared/Lockable';
import { useAPI, useDebounce, useGetUser } from 'hooks';
import {
  formatDate,
  getNormalizedOrganizationNames,
  getOrganizationDisplayLabel,
  getUserName,
  preventSubmitWithENTER,
} from 'helpers';
import Locales from 'locales';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import AddUserModal from './components/AddUserModal';
import EditUserModal from './components/EditUserModal';
import { getUserLifecycleState, USER_PAGE_SIZE } from './userManagement';

const CONST_NONE = 'none';
const CONST_FILTER_LABEL_ORGANIZATION = 'organization';
const copy = Locales.user.redesign;
const getUserAppliedFilters = (organization) =>
  organization && organization !== CONST_NONE ? { [CONST_FILTER_LABEL_ORGANIZATION]: organization } : null;

const tableHeadClassName =
  'h-[42px] border-y border-[#e3e8e5] bg-[#f7f9f8] px-[15px] text-sm font-medium text-[#5f6875]';
const tableCellClassName = 'h-[52px] px-[15px] py-0 text-sm text-[#3d434a]';
const organizationChipClassName =
  'inline-flex max-w-[140px] items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-[5px] border border-[#e6ebe7] bg-[#f8faf9] px-2 py-0.5 text-sm leading-[18px] text-[#3d434a]';

const UserOrganizations = ({ user }) => {
  const organizationNames = getNormalizedOrganizationNames(user);
  const visibleOrganizations = organizationNames.slice(0, 2);
  const hiddenOrganizations = organizationNames.slice(2);

  return (
    <div className="flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden">
      {visibleOrganizations.map((organizationName, index) => (
        <span key={`${user.id}-${organizationName}-${index}`} className={organizationChipClassName}>
          {getOrganizationDisplayLabel(organizationName)}
        </span>
      ))}

      {hiddenOrganizations.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`${organizationChipClassName} flex-none font-medium hover:border-[#aebfb2] hover:bg-[#eef3ef] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary/40`}
              aria-label={Locales.formatString(copy.templates.show_organizations, {
                count: organizationNames.length,
                name: getUserName(user.first_name, user.last_name),
              })}
            >
              +{hiddenOrganizations.length}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[250px] rounded-[9px] border-[#dee6e0] bg-white p-[15px]">
            <p className="mb-2.5 text-xs font-semibold text-[#252a30]">
              {Locales.formatString(copy.templates.organization_count, { count: organizationNames.length })}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {organizationNames.map((organizationName, index) => (
                <span key={`${user.id}-all-${organizationName}-${index}`} className={organizationChipClassName}>
                  {getOrganizationDisplayLabel(organizationName)}
                </span>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

UserOrganizations.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    organization_names: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

const User = () => {
  const { isSuperAdmin, user: currentUser } = useGetUser();
  const { api } = useAPI();
  const [cursor, setCursor] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const preSetSearch = searchParams.get('q') || '';
  const preSetOrganization = isSuperAdmin
    ? searchParams.get(CONST_FILTER_LABEL_ORGANIZATION) || CONST_NONE
    : CONST_NONE;

  const [search, setSearch] = useState(preSetSearch);
  const [selectedOrganization, setSelectedOrganization] = useState(preSetOrganization);
  const [filterOpen, setFilterOpen] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openUpdateStatusModal, setOpenUpdateStatusModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [queryFilters, setQueryFilter] = useState(() => getUserAppliedFilters(preSetOrganization));
  const debouncedSearchQuery = useDebounce(search, 1000);
  const queryClient = useQueryClient();
  const appliedOrganization = queryFilters?.[CONST_FILTER_LABEL_ORGANIZATION] || CONST_NONE;
  const effectiveSearchQuery = search ? debouncedSearchQuery : '';

  const fetchParams = useMemo(
    () => ({
      ...(queryFilters || {}),
      ...(effectiveSearchQuery ? { q: effectiveSearchQuery } : {}),
      ...(cursor ? { cursor } : {}),
      limit: USER_PAGE_SIZE,
    }),
    [cursor, effectiveSearchQuery, queryFilters]
  );
  const paginationKey = useMemo(
    () => JSON.stringify({ ...(queryFilters || {}), q: search || '' }),
    [queryFilters, search]
  );

  const { data: users, isFetching } = useQuery({
    queryFn: api.list('user/', fetchParams),
    queryKey: ['user/', fetchParams],
    keepPreviousData: true,
  });

  const { data: organizations } = useQuery({
    queryFn: api.list('organization/'),
    queryKey: ['organization/'],
    enabled: isSuperAdmin,
  });

  const organizationFilterOptions = useMemo(
    () =>
      (organizations || []).map((organization) => ({
        ...organization,
        label: getOrganizationDisplayLabel(organization),
      })),
    [organizations]
  );

  const { mutate: updateUserStatus } = useMutation({
    mutationFn: (listedUser) => {
      const endpoint = listedUser.is_active ? 'user_deactivate' : 'user_activate';
      return api.post(`user/${listedUser.id}/${endpoint}/`);
    },
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ['user/'] });
      toast.success(
        Locales.formatString(Locales.user.toast.update_success, {
          firstName: data.first_name,
          lastName: data.last_name,
          status: data.is_active ? Locales.base.status.is_activate : Locales.base.status.is_deactivate,
        })
      );
    },
  });

  const selectedOrganizationName = useMemo(() => {
    if (appliedOrganization === CONST_NONE) return null;
    const organization = organizations?.find(({ id }) => id === appliedOrganization);
    return organization ? getOrganizationDisplayLabel(organization) : null;
  }, [appliedOrganization, organizations]);

  const applyOrganizationFilter = (organizationId) => {
    setSelectedOrganization(organizationId);
    setQueryFilter(getUserAppliedFilters(organizationId));
    setFilterOpen(false);
  };

  const resetFilters = () => {
    setSelectedOrganization(CONST_NONE);
    setQueryFilter(null);
  };

  useEffect(() => {
    setSearch(preSetSearch);
    setSelectedOrganization(preSetOrganization);
    setQueryFilter(getUserAppliedFilters(preSetOrganization));
  }, [preSetOrganization, preSetSearch]);

  useEffect(() => {
    let params = {};

    if (queryFilters?.[CONST_FILTER_LABEL_ORGANIZATION]) {
      params[CONST_FILTER_LABEL_ORGANIZATION] = queryFilters[CONST_FILTER_LABEL_ORGANIZATION];
    }

    if (search) params = { ...params, q: search };
    setSearchParams(params);
  }, [queryFilters, search, setSearchParams]);

  useEffect(() => {
    setCursor('');
  }, [paginationKey]);

  return (
    <Page
      title={copy.title}
      description={isSuperAdmin ? copy.description_super_admin : copy.description_organization_admin}
      actionLabel={copy.add_action}
      actionClassName="h-[35px] rounded-lg px-[13px] text-xs shadow-none hover:shadow-none"
      onAction={() => setOpenAddModal(true)}
      removeContentStyles
    >
      {!users && <EmptyCard />}

      {users && (
        <section
          className="overflow-hidden rounded-[10px] border border-[#e3e8e5] bg-white"
          aria-label={copy.users_region}
        >
          <div className="flex min-h-[72px] flex-wrap items-center gap-2.5 p-4">
            <div className="relative min-w-[230px] flex-[0_1_395px] max-[700px]:flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#5f6875]"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder={copy.search_placeholder}
                aria-label={copy.search_placeholder}
                autoComplete="off"
                className="h-[37px] rounded-[7px] border-[#dfe5e1] bg-white pl-9 pr-9 text-xs text-[#3d434a] placeholder:text-[#5f6875] focus-visible:border-hajj-primary focus-visible:ring-2 focus-visible:ring-hajj-primary/10 focus-visible:ring-offset-0"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={preventSubmitWithENTER}
              />
              {search && (
                <button
                  type="button"
                  className="absolute right-1.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-[#5f6875] hover:bg-[#eef3ef] hover:text-[#326346] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary/40"
                  aria-label={copy.clear_search}
                  onClick={() => setSearch('')}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </button>
              )}
            </div>

            {isSuperAdmin && (
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    type="button"
                    className={`hidden h-[37px] rounded-lg border-[#dce3de] px-3 text-xs shadow-none ${
                      appliedOrganization !== CONST_NONE
                        ? 'border-[#9abcaa] bg-[#f3f8f5] text-[#216341] hover:bg-[#eef5f0]'
                        : ''
                    }`}
                  >
                    <SlidersHorizontal aria-hidden="true" />
                    {copy.filter_action}
                    {appliedOrganization !== CONST_NONE && (
                      <span className="inline-flex size-4 items-center justify-center rounded bg-hajj-primary text-[10px] text-white">
                        1
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[270px] rounded-[9px] border-[#dee6e0] bg-white p-4 shadow-[0_5px_16px_rgba(30,53,37,0.06)]"
                >
                  <label htmlFor="organization-filter" className="mb-2.5 block text-xs font-semibold text-[#252a30]">
                    {copy.organization}
                  </label>
                  <Select value={selectedOrganization} onValueChange={applyOrganizationFilter}>
                    <SelectTrigger
                      id="organization-filter"
                      className="h-[37px] rounded-md border-[#dce4de] bg-white px-[9px] text-xs text-[#3d434a] focus:ring-hajj-primary/20 focus:ring-offset-0"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CONST_NONE}>{copy.all_organizations}</SelectItem>
                      {organizationFilterOptions.map((organization) => (
                        <SelectItem key={organization.id} value={organization.id}>
                          {organization.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mb-0 mt-[11px] text-[11px] leading-4 text-[#5f6875]">{copy.filter_help}</p>
                </PopoverContent>
              </Popover>
            )}

            {isSuperAdmin && appliedOrganization !== CONST_NONE && (
              <button
                type="button"
                className="flex items-center gap-1 rounded-[5px] border border-[#dfe8e2] bg-[#f7faf8] px-[7px] py-1 text-[11px] text-[#53705e] hover:border-[#aebfb2] hover:bg-[#eef3ef] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary/40"
                aria-label={copy.remove_organization_filter}
                onClick={resetFilters}
              >
                {selectedOrganizationName || copy.organization}
                <X className="size-3" aria-hidden="true" />
              </button>
            )}

            <span className="ml-auto whitespace-nowrap text-xs text-[#536157]" aria-live="polite">
              {Locales.formatString(copy.templates.user_count, {
                count: users.count,
                suffix: users.count === 1 ? '' : 's',
              })}
            </span>
          </div>

          <div role="region" aria-label={copy.table_region}>
            <Table className={`table-fixed ${isSuperAdmin ? 'min-w-[855px]' : 'min-w-[650px]'}`}>
              <TableCaption className="sr-only">{copy.table_caption}</TableCaption>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className={`${tableHeadClassName} ${isSuperAdmin ? 'w-[20%]' : 'w-[26%]'}`}>
                    {copy.columns.name}
                  </TableHead>
                  <TableHead className={`${tableHeadClassName} ${isSuperAdmin ? 'w-[27%]' : 'w-[43%]'}`}>
                    {copy.columns.email}
                  </TableHead>
                  {isSuperAdmin && (
                    <TableHead className={`${tableHeadClassName} w-[26%]`}>{copy.organizations}</TableHead>
                  )}
                  <TableHead className={`${tableHeadClassName} ${isSuperAdmin ? 'w-[10%]' : 'w-[12%]'}`}>
                    {copy.columns.status}
                  </TableHead>
                  <TableHead className={`${tableHeadClassName} ${isSuperAdmin ? 'w-[11%]' : 'w-[12%]'}`}>
                    {copy.columns.created}
                  </TableHead>
                  <TableHead
                    className={`${tableHeadClassName} ${
                      isSuperAdmin ? 'w-[6%]' : 'w-[7%]'
                    } text-center max-[700px]:sticky max-[700px]:right-0 max-[700px]:z-10 max-[700px]:shadow-[-5px_0_8px_-7px_rgba(30,53,37,0.35)]`}
                  >
                    {copy.columns.actions}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((listedUser) => (
                  <TableRow key={listedUser.id} className="hover:bg-[#fafcfb]">
                    <TableCell className={tableCellClassName}>
                      <div className="flex min-w-0 items-center gap-[7px]">
                        <span className="block overflow-hidden text-ellipsis whitespace-nowrap font-medium text-[#252a30]">
                          {getUserName(listedUser.first_name, listedUser.last_name)}
                        </span>
                        {currentUser?.id === listedUser.id && (
                          <span className="flex-none rounded bg-[#edf5ef] px-[5px] text-[11px] font-medium leading-[18px] text-[#316c48]">
                            {copy.current_user}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={tableCellClassName}>
                      <span className="block overflow-hidden text-ellipsis whitespace-nowrap">{listedUser.email}</span>
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell className={tableCellClassName}>
                        <UserOrganizations user={listedUser} />
                      </TableCell>
                    )}
                    <TableCell className={tableCellClassName}>
                      <span
                        className={`inline-flex h-[25px] items-center gap-[5px] whitespace-nowrap rounded-[5px] px-2 text-sm font-medium ${
                          getUserLifecycleState(listedUser).isEffectivelyActive
                            ? 'bg-[#eaf6ee] text-[#35734e]'
                            : 'bg-[#fcf0f0] text-[#a34242]'
                        }`}
                      >
                        {getUserLifecycleState(listedUser).isEffectivelyActive ? copy.active : copy.inactive}
                      </span>
                    </TableCell>
                    <TableCell className={`${tableCellClassName} tabular-nums`}>
                      {formatDate(listedUser.created)}
                    </TableCell>
                    <TableCell
                      className={`${tableCellClassName} text-center max-[700px]:sticky max-[700px]:right-0 max-[700px]:z-[5] max-[700px]:bg-white max-[700px]:shadow-[-5px_0_8px_-7px_rgba(30,53,37,0.35)]`}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-[30px] rounded-md text-[#5f6875] hover:bg-[#eef3ef] hover:text-[#326346]"
                            aria-label={Locales.formatString(copy.templates.actions_for, {
                              name: getUserName(listedUser.first_name, listedUser.last_name),
                            })}
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-[164px] rounded-[9px] border-[#dee6e0] bg-white p-1.5 shadow-[0_5px_16px_rgba(30,53,37,0.06)]"
                        >
                          <DropdownMenuGroup>
                            <LockableDropdownMenuItem
                              className="h-[35px] rounded-[5px] px-[9px] text-xs text-[#3d434a] focus:bg-[#f5f8f6]"
                              onSelect={() => {
                                setSelectedItem(listedUser);
                                setOpenEditModal(true);
                              }}
                            >
                              <Pen className="text-[#5f6875]" />
                              {copy.edit}
                            </LockableDropdownMenuItem>
                            {getUserLifecycleState(listedUser).canToggleStatus && (
                              <>
                                <DropdownMenuSeparator className="mx-px my-1 bg-[#eaf0eb]" />
                                <LockableDropdownMenuItem
                                  className={`h-[35px] rounded-[5px] px-[9px] text-xs focus:bg-[#f5f8f6] ${
                                    listedUser.is_active ? 'text-[#a34838]' : 'text-hajj-primary'
                                  }`}
                                  onSelect={() => {
                                    setSelectedItem(listedUser);
                                    setOpenUpdateStatusModal(true);
                                  }}
                                >
                                  {listedUser.is_active ? <Ban /> : <RotateCw />}
                                  {listedUser.is_active ? copy.deactivate : copy.activate}
                                </LockableDropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}

                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 6 : 5} className="h-[206px] text-center">
                      <div className="flex flex-col items-center gap-2 text-[#5f6875]">
                        <Search className="size-[22px] text-[#91a095]" aria-hidden="true" />
                        <strong className="font-medium text-[#252a30]">{copy.empty_title}</strong>
                        <span className="text-xs">{copy.empty_description}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            key={paginationKey}
            data={users}
            isFetching={isFetching}
            limit={USER_PAGE_SIZE}
            setCursor={setCursor}
          />
        </section>
      )}

      {openAddModal && (
        <AddUserModal
          isOpen={openAddModal}
          onClose={() => setOpenAddModal(false)}
          organizations={organizations || []}
        />
      )}

      {selectedItem && (
        <>
          {openEditModal && (
            <EditUserModal
              isOpen={openEditModal}
              onClose={() => setOpenEditModal(false)}
              user={selectedItem}
              organizations={organizations || []}
            />
          )}

          {openUpdateStatusModal && (
            <ConfirmationModal
              isOpen={openUpdateStatusModal}
              setOpenModal={setOpenUpdateStatusModal}
              title={selectedItem.is_active ? Locales.user.disable_account : Locales.user.enable_account}
              confirmBtnVariant={selectedItem.is_active ? 'destructive' : 'hajj'}
              confirmBtnLabel={selectedItem.is_active ? Locales.base.button.disable : Locales.base.button.enable}
              message={
                <span>
                  {Locales.formatString(Locales.user.alert_confirm, {
                    action: selectedItem.is_active ? Locales.base.status.deactivate : Locales.base.status.activate,
                  })}
                  <strong>{getUserName(selectedItem.first_name, selectedItem.last_name)}</strong>{' '}
                  {Locales.user.associated_mail}
                  <span>{selectedItem.email}</span> ?
                </span>
              }
              onConfirm={() => updateUserStatus(selectedItem)}
            />
          )}
        </>
      )}
    </Page>
  );
};

export default User;
